// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Button,
  Dialog,
  EmptyState,
  FormField,
  KycStatusBadge,
  Select,
  Skeleton,
  Textarea,
} from "@chotca/ui";
import type { ChangeRejectCode } from "@chotca/config";
import {
  useAdminOrgKyc,
  useApproveOrgKyc,
  useRejectOrgKyc,
} from "@chotca/api-client";

// Standardized reject codes (SCRUM-74, spec §7.4). Same set the org-KYC reviewer
// picks from; OTHER requires a free-text detail.
const REJECT_CODES: { value: ChangeRejectCode; label: string }[] = [
  { value: "DOC_UNREADABLE", label: "Ảnh giấy tờ không đọc được" },
  { value: "DOC_EXPIRED", label: "Giấy tờ đã hết hạn" },
  { value: "NAME_MISMATCH", label: "Tên không khớp giấy phép" },
  { value: "TAXCODE_MISMATCH", label: "Mã số thuế không khớp" },
  { value: "WRONG_DOC_TYPE", label: "Sai loại giấy tờ" },
  { value: "REP_NOT_VERIFIED", label: "Người đại diện chưa xác minh CCCD" },
  { value: "OTHER", label: "Lý do khác (nhập chi tiết)" },
];

export function OrgDetailClient({ id }: { id: string }) {
  const nav = useNavigate();
  const { data, isLoading, isError, refetch } = useAdminOrgKyc(id);
  const approve = useApproveOrgKyc();
  const reject = useRejectOrgKyc();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [code, setCode] = useState<ChangeRejectCode>("DOC_UNREADABLE");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState<string | null>(null);

  function openReject() {
    setCode("DOC_UNREADABLE");
    setDetail("");
    setError(null);
    setRejectOpen(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 rounded-md" />
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <EmptyState
        title="Không tải được hồ sơ"
        action={<Button onClick={() => refetch()}>Thử lại</Button>}
      />
    );
  }

  const status = data.status;
  const isPending = status === "PENDING";

  const handleApprove = async () => {
    setError(null);
    try {
      await approve.mutateAsync(id);
      nav("/kyc/orgs");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    }
  };

  const handleReject = async () => {
    setError(null);
    if (code === "OTHER" && !detail.trim()) {
      setError("Vui lòng nhập chi tiết cho lý do khác.");
      return;
    }
    try {
      const label = REJECT_CODES.find((c) => c.value === code)?.label ?? code;
      await reject.mutateAsync({
        id,
        reason: code === "OTHER" ? detail.trim() : label,
        rejectCode: code,
      });
      setRejectOpen(false);
      nav("/kyc/orgs");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{data.legalName}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-ink/60">
            <KycStatusBadge status={status} />
            <span>
              · Submitted {new Date(data.submittedAt).toLocaleString("vi-VN")}
            </span>
          </div>
        </div>
      </header>

      <section>
        <h2 className="mb-3 text-sm uppercase text-ink/60">Tài liệu</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <DocTile
            label="Giấy đăng ký kinh doanh"
            url={data.registrationDocSignedUrl}
          />
          {data.taxDocSignedUrl ? (
            <DocTile
              label="Tài liệu thuế (tùy chọn)"
              url={data.taxDocSignedUrl}
            />
          ) : null}
        </div>
        <p className="mt-2 text-xs text-ink/50">
          URL ảnh hết hạn lúc{" "}
          {new Date(data.signedUrlExpiresAt).toLocaleTimeString("vi-VN")}.
        </p>
      </section>

      <section className="rounded-md border border-ink/10 bg-paper p-5">
        <h2 className="mb-4 text-sm uppercase text-ink/60">
          Thông tin khai báo
        </h2>
        <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Tên pháp lý" value={data.legalName} />
          <Field label="Mã số thuế" value={data.taxCode} mono />
          <Field label="Người đại diện" value={data.representativeName} />
          <Field label="Loại hình kinh doanh" value={data.businessType} />
          <Field
            label="Submitted by user"
            value={data.submittedByUserId}
            mono
          />
          <Field
            label="Reviewed"
            value={
              data.reviewedAt
                ? new Date(data.reviewedAt).toLocaleString("vi-VN")
                : "—"
            }
          />
        </dl>
      </section>

      {data.rejectReason ? (
        <section className="rounded-md border-l-4 border-ember bg-ember/5 p-4">
          <p className="text-xs font-bold uppercase text-ember">
            Lý do từ chối lần trước
          </p>
          <p className="mt-1 text-sm text-ink">{data.rejectReason}</p>
        </section>
      ) : null}

      {error ? (
        <p className="rounded-md bg-ember/10 px-3 py-2 text-sm text-ember">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-end gap-3 border-t border-ink/10 pt-4">
        <Button
          variant="ghost"
          onClick={openReject}
          disabled={!isPending || approve.isPending || reject.isPending}
        >
          Từ chối
        </Button>
        <Button
          variant="secondary"
          onClick={handleApprove}
          disabled={!isPending || approve.isPending || reject.isPending}
          loading={approve.isPending}
        >
          Duyệt
        </Button>
      </footer>

      <Dialog
        open={rejectOpen}
        onClose={() => {
          if (!reject.isPending) setRejectOpen(false);
        }}
        title="Từ chối hồ sơ"
        description="Chọn mã lý do chuẩn hoá. Owner sẽ thấy hướng dẫn khắc phục."
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setRejectOpen(false)}
              disabled={reject.isPending}
            >
              Huỷ
            </Button>
            <Button
              variant="urgent"
              onClick={handleReject}
              loading={reject.isPending}
            >
              Xác nhận từ chối
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <FormField label="Mã lý do">
            <Select
              value={code}
              onValueChange={(v) => setCode(v as ChangeRejectCode)}
              options={REJECT_CODES}
            />
          </FormField>
          {code === "OTHER" ? (
            <FormField label="Chi tiết">
              <Textarea
                rows={3}
                maxLength={500}
                placeholder="Mô tả lý do cụ thể…"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />
            </FormField>
          ) : null}
          {error ? <p className="text-xs text-ember">{error}</p> : null}
        </div>
      </Dialog>
    </div>
  );
}

function DocTile({
  label,
  url,
}: {
  label: string;
  url: string | null | undefined;
}) {
  const isPdf = url?.includes(".pdf");
  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden rounded-md border border-ink/10 bg-mist"
    >
      {url && !isPdf ? (
        <img
          src={url}
          alt={label}
          className="aspect-[1/1.4] w-full object-contain bg-mist"
        />
      ) : (
        <div className="flex aspect-[1/1.4] items-center justify-center text-sm text-ink/40">
          {url ? "PDF — bấm để mở" : "(no url)"}
        </div>
      )}
      <div className="border-t border-ink/10 bg-paper px-3 py-2 text-xs font-medium text-ink/70">
        {label} · mở tài liệu đầy đủ
      </div>
    </a>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase text-ink/60">{label}</dt>
      <dd className={mono ? "mt-1 font-mono text-sm" : "mt-1 text-sm"}>
        {value}
      </dd>
    </div>
  );
}
